<template>
  <div
    class="columns is-flex-wrap-wrap member-form"
    @click="$emit('click')">
    <div
      v-if="isCreateMode"
      class="column is-full member-form__header clickable">
      <span>{{ index + 1 }}.</span>
      <div class="member-form__header--right">
        <a v-if="index > 0" href="#" @click.prevent="onRemove">
          <span class="icon-trash" />
        </a>
        <a
          href="#"
          :disabled="isActive"
          @click.prevent="$emit('click')">
          <span
            class="ml-6"
            :class="isActive ? 'icon-up-chevron' : 'icon-right-chevron'" />
        </a>
      </div>
    </div>
    <div class="column is-two-thirds">
      <slot name="title" />
      <b-field horizontal label="人員編號">
        <b-input v-model="member.member_detail.serial" name="serial" expanded />
      </b-field>
      <b-field
        horizontal
        :type="{ 'is-danger': invalidAccount }"
        :message="accountFieldMessage">
        <template #label>
          <span class="required">帳號</span>
        </template>
        <div class="member-form__account-field">
          <b-input
            v-model="member.account"
            name="account"
            class="member-form__account-field--input"
            expanded
            :disabled="!isCreateMode"
            @input="v$.member.account.$touch" />
        </div>
      </b-field>
      <b-field horizontal class="member-form__role">
        <template #label>
          角色<b-tooltip
            multilined
            type="is-dark"
          >
            <i class="icon-help-circle-outline text-gray-500" />
            <template v-slot:content>
              <ol>
                <li>一般身份僅可使用前台。</li>
                <li>團主可新增、編輯訂購及店家。</li>
                <li>管理員權限包含團主權限、新增人員及統計報表。</li>
              </ol>
            </template>
          </b-tooltip>
        </template>
        <b-radio
          v-for="role in filteredRoleMapping"
          v-model="member.role"
          :name="`role-${index}`"
          :native-value="role.key"
          :disabled="!isRoleAvailable(role.key)"
          :key="`role-${role.key}`">
          {{ role.name }}
        </b-radio>
      </b-field>
      <b-field
        horizontal
        :type="{ 'is-danger': invalidName }"
        :message="nameFieldMessage">
        <template #label>
          <span class="required">姓名</span>
        </template>
        <b-input
          v-model="member.member_detail.name"
          name="name"
          expanded
          @input="v$.member.member_detail.name.$touch" />
      </b-field>
      <template v-if="isActive">
        <b-field horizontal label="部門">
          <b-select
            v-model="member.department_id"
            placeholder="部門"
            class="member-form__department"
            :disabled="!departments.length"
            expanded>
            <option
              v-for="department in departments"
              :value="department.id"
              :key="department.id">
              {{ department.name }}
            </option>
          </b-select>
        </b-field>
      </template>
      <b-field
        horizontal
        :type="{ 'is-danger': invalidEmail }"
        :message="emailFieldMessage">
        <template #label>
          <span>Email</span>
        </template>
        <div class="member-form__email-field">
          <b-input
            v-model="member.email"
            name="email"
            type="email"
            class="member-form__email-field--input"
            expanded
            @input="v$.member.email.$touch" />
        </div>
      </b-field>
      <div v-if="isActive" class="member-form__detail">
        <b-field
          horizontal
          :type="{ 'is-danger': invalidExtension }"
          :message="extensionFieldMessage"
          label="分機/電話">
          <b-input v-model="member.member_detail.ext" name="ext" expanded />
        </b-field>
        <b-field
          horizontal
          :type="{ 'is-danger': invalidMobile }"
          :message="mobileFieldMessage"
          label="手機號碼">
          <b-input
            v-model="member.member_detail.phone"
            expanded
            name="phone"
            type="tel"
            @input="v$.member.member_detail.phone.$touch"
          />
        </b-field>
        <b-field
          horizontal
          :type="{ 'is-danger': invalidAllowOrderOnSite }"
          :message="allowOrderOnSiteFieldMessage">
          <template #label>
            <span class="required">可現場訂單次數</span>
          </template>
          <div class="member-form____allow-order-field">
            <b-input
              v-model="member.allow_order_on_site"
              name="allow_order_on_site"
              type="allow_order_on_site"
              class="member-form__allow-order-field--input"
              expanded
              @keypress.native="onInputNonDigit"
              @input="v$.member.allow_order_on_site.$touch" />
          </div>
        </b-field>
        <b-field horizontal label="備註">
          <b-input
            v-model="member.member_detail.note"
            :has-counter="false"
            name="note"
            maxlength="40"
            placeholder="可以記錄關於人員的點點滴滴"
            expanded />
        </b-field>
      </div>
    </div>
    <div
      v-if="$slots.action"
      class="column is-full">
      <slot name="action"></slot>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import useVuelidate from '@vuelidate/core';

import {
  account as accountRule,
  required,
  email,
  phone,
  maxValue,
  numeric,
} from '@/utils/validators';
import {
  MEMBER_MODE,
  NO_DEPARTMENT_ID,
  USER_LEVEL,
  USER_LEVEL_MAPPING,
} from '@/utils/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';

export default {
  name: 'MemberForm',
  props: {
    hasEmptyDepartment: {
      type: Boolean,
      default: true,
    },
    index: {
      type: Number,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isDuplicated: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    value: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editEmail: false,
      roleMapping: USER_LEVEL_MAPPING,
      departments: [],
    };
  },
  async mounted() {
    const departments = await this.getDepartmentList();
    this.departments = this.hasEmptyDepartment
      ? departments : departments.filter((d) => d.id !== NO_DEPARTMENT_ID);
    console.log(this.departments, this.hasEmptyDepartment);
  },
  computed: {
    member: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isOwner() {
      return this.member.role === USER_LEVEL.OWNER;
    },
    enabledRoles() {
      const roles = Object.values(USER_LEVEL).filter((l) => l < this.currentRole);

      return roles;
    },
    filteredRoleMapping() {
      const ownerAvailable = this.isOwner && this.isEditMode;

      if (!ownerAvailable) {
        return this.roleMapping.filter((item) => item.key !== USER_LEVEL.OWNER);
      }
      return this.roleMapping;
    },
    nameFieldMessage() {
      return this.v$.member.member_detail.name?.$errors?.[0]?.$message || '';
    },
    accountFieldMessage() {
      if (this.isDuplicated) {
        return '帳號重複輸入';
      }
      const errorMsg = this.v$.member.account?.$errors?.[0]?.$message || '';
      if (this.isCreateMode && !errorMsg) {
        return '登入帳號限使用英文字母、數字或符號';
      }
      return errorMsg;
    },
    allowOrderOnSiteFieldMessage() {
      return this.v$.member.allow_order_on_site?.$errors?.[0]?.$message || '需為0或正整數字';
    },
    emailFieldMessage() {
      return this.v$.member.email?.$errors?.[0]?.$message || '';
    },
    extensionFieldMessage() {
      return this.v$.member.member_detail.ext?.$errors?.[0]?.$message || '';
    },
    mobileFieldMessage() {
      return this.v$.member.member_detail.phone?.$errors?.[0]?.$message || '';
    },
    isCreateMode() {
      return this.mode === MEMBER_MODE.CREATE;
    },
    isEditMode() {
      return this.mode === MEMBER_MODE.EDIT;
    },
    invalidAccount() {
      const target = this.v$.member.account;
      if (this.isDuplicated) {
        return true;
      }
      return target.$dirty && target.$invalid;
    },
    invalidAllowOrderOnSite() {
      const target = this.v$.member.allow_order_on_site;
      return target.$dirty && target.$invalid;
    },
    invalidName() {
      const target = this.v$.member.member_detail?.name;
      return target.$dirty && target.$invalid;
    },
    invalidEmail() {
      const target = this.v$.member.email;
      return target.$dirty && target.$invalid;
    },
    invalidExtension() {
      const target = this.v$.member.member_detail?.ext;
      return target.$dirty && target.$invalid;
    },
    invalidMobile() {
      const target = this.v$.member.member_detail?.phone;
      return target.$dirty && target.$invalid;
    },
    ...mapGetters('user', ['currentRole']),
  },
  watch: {
    member: {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },
  },
  methods: {
    onRemove() {
      this.$emit('remove');
    },
    isRoleAvailable(newRole) {
      if (this.currentRole < this.member.role) {
        /* e.g. Admin can't assign new role for other admin */
        return false;
      }
      if (this.isOwner) {
        /* Ownership transition should be done in Setting  */
        return false;
      }
      return Object.values(USER_LEVEL).filter((l) => l <= this.currentRole).includes(newRole);
    },
    onInputNonDigit(event) {
      if (!/\d/.test(event.key)) {
        event.preventDefault();
      }
    },
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
  },
  validations() {
    return {
      member: {
        account: {
          accountRule,
          required: required('帳號'),
        },
        allow_order_on_site: {
          numeric,
          required: required('可現場訂單次數'),
          maxValue: maxValue('可現場訂單次數', 9999),
        },
        email: {
          email,
        },
        member_detail: {
          name: { required: required('姓名') },
          ext: { phone },
          phone: { numeric },
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.member-form {
  margin: 1rem 0;
  padding: 1rem;
  box-shadow: 0px 0px 4px 0px $lighter-grey;
  transition: box-shadow 0.2s ease-in-out;
  border-radius: 4px;
  &:hover {
    box-shadow: 0px 0px 10px 0px $lighter-grey;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0 .75rem .75rem .75rem;
    border-bottom: 1px solid $lighter-grey;
    &.clickable {
      cursor: pointer;
    }
    &--right {
      font-size: 1.25rem;
      span, svg {
        color: $dark-grey;
      }
    }
  }

  &__account-field, &__allow-order-field, &__email-field {
    display: flex;
    &--input {
      width: 100%;
    }
  }

  &__department {
    ::v-deep .select::after {
      border-color: $dark-grey;
    }
  }

  &__role {
    ::v-deep .b-tooltip {
      .tooltip-trigger {
        cursor: pointer;
        i::before {
          color: $dark-grey;
        }
      }
      .tooltip-content {
        ol {
          margin: 0;
          padding-left: 1rem;
          text-align: left;
          list-style: auto;
        }
      }
    }
  }
}

::v-deep .field-body {
  flex-grow: 3;
}

::v-deep .field-label {
  margin-right: 0;
  text-align: left;
  label {
    color: $dark-grey;
    font-weight: 400;
  }
  .required {
    &::after {
      content: '\204e';
      margin-left: 3px;
      color: $red;
      vertical-align: text-bottom;
    }
  }
}
</style>
